import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPasswordProtected from './components/AdminPasswordProtected';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import JobDetails from './pages/JobDetails';
import Applications from './pages/Applications';
import TaskAssessment from './pages/TaskAssessment';
import AdminDashboard from './pages/AdminDashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import CreateJob from './pages/CreateJob';
import EditJob from './pages/EditJob';
import ManageJobs from './pages/ManageJobs';
import Tasks from './pages/Tasks';
import CreateTask from './pages/CreateTask';
import EditTask from './pages/EditTask';
import TaskSubmissions from './pages/TaskSubmissions';
import Profile from './pages/Profile';
import ManageApplications from './pages/ManageApplications';
import AdminUsers from './pages/AdminUsers';
import ScheduleInterview from './pages/ScheduleInterview';
import AdminLanding from './pages/AdminLanding';
import HomePage from './pages/HomePage';

// Components
import Layout from './components/Layout';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#ff9800',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <AuthProvider>
            <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/admin-login" element={<Login isAdmin={true} />} />
                <Route path="/admin-register" element={<Register isAdmin={true} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                
                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    {/* Common Routes */}
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/admin" element={
                      <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
                        <AdminPasswordProtected>
                          <AdminLanding />
                        </AdminPasswordProtected>
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics-dashboard" element={
                      <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
                        <AdminPasswordProtected>
                          <AnalyticsDashboard />
                        </AdminPasswordProtected>
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={<Profile />} />
                    
                    {/* Candidate Routes */}
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/jobs/:id" element={<JobDetails />} />
                    <Route path="/my-applications" element={<Applications />} />
                    <Route path="/assessment/:applicationId/:taskId" element={<TaskAssessment />} />
                    
        {/* Admin/Recruiter Routes */}
        <Route path="/admin-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <AdminDashboard />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/create-job" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <CreateJob />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/edit-job/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <EditJob />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/manage-jobs" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <ManageJobs />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        
        {/* Task Management Routes */}
        <Route path="/tasks" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <Tasks />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/create-task" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <CreateTask />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/edit-task/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <EditTask />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/task-submissions/:taskId" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <TaskSubmissions />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
        <Route path="/task-submissions" element={
          <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
            <AdminPasswordProtected>
              <TaskSubmissions />
            </AdminPasswordProtected>
          </ProtectedRoute>
        } />
                    <Route path="/admin/applications" element={
                      <ProtectedRoute allowedRoles={['admin', 'recruiter', 'interviewer']}>
                        <AdminPasswordProtected>
                          <ManageApplications />
                        </AdminPasswordProtected>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/users" element={
                      <ProtectedRoute allowedRoles={['admin', 'recruiter']}>
                        <AdminPasswordProtected>
                          <AdminUsers />
                        </AdminPasswordProtected>
                      </ProtectedRoute>
                    } />
                    <Route path="/schedule-interview/:applicationId" element={
                      <ProtectedRoute allowedRoles={['admin', 'recruiter', 'interviewer']}>
                        <AdminPasswordProtected>
                          <ScheduleInterview />
                        </AdminPasswordProtected>
                      </ProtectedRoute>
                    } />
                  </Route>
                </Route>
                
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
